import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext
} from 'vue-router'

import { loadLocaleMessages, setI18nLanguage } from '@/i18n'

import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

const checkRestrictedGroups = (userGroups: Array<string>, restrictedGroups: Array<string>) =>
  userGroups.some(x => restrictedGroups.includes(x))

const init = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const locale = to.params.locale as string
  if (locale) {
    const appStore = useAppStore()
    const availableLocales = appStore.availableLanguages.map(l => l.locale) || []
    if (!availableLocales.includes(locale)) {
      return next({ name: 'void_entry' })
    }
    await loadLocaleMessages(locale)
    setI18nLanguage(locale)
  }

  const authStore = useAuthStore()
  authStore.initProvider()
  await authStore.checkUser()

  if (to.meta && to.meta.requireAuth === true && !authStore.isAuthenticated)
    return next({ name: 'login', params: { locale: to.params.locale } })

  if (to.name === 'login' && authStore.isAuthenticated) return next({ name: 'void_entry' })

  if (
    to.meta &&
    Array.isArray(to.meta.restrictedGroups) &&
    authStore.isAuthenticated &&
    authStore.userData &&
    Array.isArray(authStore.userData.groups) &&
    !checkRestrictedGroups(authStore.userData.groups, to.meta.restrictedGroups)
  )
    return next({ name: 'void_entry' })

  return next()
}

const checkLocale = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const appStore = useAppStore()
  next({ name: 'home', params: { locale: appStore.defaultLanguage } })
}

const routes: Array<RouteRecordRaw> = [
  {
    name: 'void_entry',
    path: '/',
    component: () => import('@/views/MainView.vue'),
    beforeEnter: checkLocale
  },
  {
    name: 'main',
    path: '/:locale',
    component: () => import('@/views/MainView.vue'),
    meta: {
      requireAuth: true,
      sidebar: true,
      topbar: true
    },
    children: [
      {
        name: 'home',
        path: '',
        redirect: { name: 'orders' }
      },
      {
        name: 'orders',
        path: 'orders',
        component: () => import('@/views/OrdersView.vue')
      },
      {
        name: 'dealers',
        path: 'dealers',
        component: () => import('@/views/DealersView.vue'),
        meta: {
          restrictedGroups: ['ADMIN']
        }
      },
      {
        name: 'parameters',
        path: 'parameters',
        component: () => import('@/views/Parameters/ParametersView.vue'),
        meta: {
          restrictedGroups: ['ADMIN']
        },
        children: [
          {
            name: 'parametersHome',
            path: '',
            redirect: { name: 'parametersLogo' }
          },
          {
            name: 'parametersLogo',
            path: 'logo',
            component: () => import('@/views/Parameters/ParametersLogoView.vue')
          },
          {
            name: 'parametersProducts',
            path: 'products',
            component: () => import('@/views/Parameters/ParametersProductsView.vue')
          },
          {
            name: 'parametersSurcharges',
            path: 'surcharges',
            component: () => import('@/views/Parameters/ParametersSurchargesView.vue')
          },
          {
            name: 'parametersMaterials',
            path: 'materials',
            component: () => import('@/views/Parameters/ParametersMaterialsView.vue')
          }
        ]
      },
      {
        name: 'webRequests',
        path: 'web-requests',
        component: () => import('@/views/WebRequestsView.vue')
      },
      {
        name: 'configurator',
        path: 'configurator/:id',
        component: () => import('@/views/ConfiguratorView.vue'),
        meta: {
          topbar: false
        }
      },
      {
        name: 'b2cStart',
        path: 'b2c/start/:token',
        component: () => import('@/views/B2CStartView.vue'),
        meta: {
          requireAuth: false,
          topbar: false,
          b2c: true
        }
      },
      {
        name: 'b2cConfigurator',
        path: 'b2c/customize/:token',
        component: () => import('@/views/ConfiguratorView.vue'),
        meta: {
          requireAuth: false,
          topbar: false,
          b2c: true
        }
      },
      {
        name: 'login',
        path: 'login',
        component: () => import('@/views/LoginView.vue'),
        meta: {
          requireAuth: false,
          topbar: false
        }
      }
    ]
  },
  {
    name: 'catchAll',
    path: '/:locale/:pathMatch(.*)*',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(init)

export default router
